// Strava links and Strava activity summaries, without the Strava API (decision log: no API, no scraping).
// - A link to one Strava activity comes from activities.csv (the activity id), from Android's share sheet
//   (the Strava app shares "https://strava.app.link/..." or "https://www.strava.com/activities/<id>") or is pasted.
//   Canonical links are stored as the id; short links are stored as given and never resolved (resolving would
//   fetch a Strava page).
// - "Open in Strava" uses https://www.strava.com/activities/<id>, which opens the Strava app on phones (universal
//   link / app link) and the website elsewhere.
// - The MCP coach imports summaries that the person's own AI read through the Strava connector (import_activities).
import type { CardioSport } from '../types';
import { activityProblem, type CardioActivity } from './activity';
import { sameActivity, sportFromStrava } from './import';

/** Strava activity ids are positive integers (about 11 digits in 2026). */
const STRAVA_ID = /^[1-9]\d{0,19}$/;
export const isStravaId = (v: unknown): v is string => typeof v === 'string' && STRAVA_ID.test(v);

/** Hosts of Strava's own short links (Branch links the app shares). Stored as given, never opened by us. */
const SHORT_HOSTS = ['strava.app.link'];
const SITE_HOSTS = ['strava.com', 'www.strava.com', 'm.strava.com'];
export const MAX_STRAVA_URL = 300;

/** What a Strava link says: the activity id (canonical links) or the short link as given. */
export type StravaLink = { id: string; url?: undefined } | { id?: undefined; url: string };

/** The link to open an activity in Strava (app on phones, website elsewhere). */
export const stravaActivityUrl = (id: string): string => `https://www.strava.com/activities/${id}`;

/** The address of a link: the canonical activity URL for an id, else the short link as given. */
export const stravaLinkUrl = (link: StravaLink): string => (link.id !== undefined ? stravaActivityUrl(link.id) : link.url);

/** One URL to a Strava link, or null when it is not a link to one Strava activity. */
export function parseStravaUrl(raw: string): StravaLink | null {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  if (u.username || u.password || (u.port && u.port !== '443' && u.port !== '80')) return null;
  const host = u.hostname.toLowerCase();
  if (SITE_HOSTS.includes(host)) {
    const m = /^\/activities\/(\d{1,20})(?:\/[a-z_-]*)?\/?$/i.exec(u.pathname);
    return m && isStravaId(m[1]) ? { id: m[1]! } : null;
  }
  if (SHORT_HOSTS.includes(host)) {
    // A short code only: https://strava.app.link/AbC123xyz (no query, it can carry tracking data).
    if (!/^\/[A-Za-z0-9_-]{4,40}\/?$/.test(u.pathname)) return null;
    const url = `https://${host}${u.pathname.replace(/\/$/, '')}`;
    return url.length <= MAX_STRAVA_URL ? { url } : null;
  }
  return null;
}

/**
 * The first Strava activity link in a text (what the share sheet or the clipboard holds, for example
 * "Check out my activity on Strava: https://strava.app.link/AbC123"), or null.
 */
export function findStravaLink(text: string | null | undefined): StravaLink | null {
  if (!text) return null;
  const words = text.slice(0, 4000).match(/https?:\/\/[^\s<>"']+/gi) ?? [];
  for (const w of words) {
    const hit = parseStravaUrl(w.replace(/[).,;!?]+$/, ''));
    if (hit) return hit;
  }
  return null;
}

/** The link stored on an activity, ready for an href, or null. Only our own two shapes ever come out. */
export function stravaHref(a: Pick<CardioActivity, 'stravaId' | 'stravaUrl'> | null | undefined): string | null {
  if (!a) return null;
  if (isStravaId(a.stravaId)) return stravaActivityUrl(a.stravaId);
  if (typeof a.stravaUrl === 'string') {
    const parsed = parseStravaUrl(a.stravaUrl);
    if (parsed?.url) return parsed.url;
    if (parsed?.id) return stravaActivityUrl(parsed.id);
  }
  return null;
}

/** The activity with the Strava link added (id for canonical links, else the short link). */
export function withStravaLink<T extends CardioActivity>(a: T, link: StravaLink): T {
  const rest = { ...a };
  delete rest.stravaId;
  delete rest.stravaUrl;
  return link.id !== undefined ? { ...rest, stravaId: link.id } : { ...rest, stravaUrl: link.url };
}

/** Whether two links point to the same activity (same id, or the same short link). */
export function sameStravaLink(a: Pick<CardioActivity, 'stravaId' | 'stravaUrl'>, link: StravaLink): boolean {
  return link.id ? a.stravaId === link.id : !!link.url && a.stravaUrl === link.url;
}

// Summaries from the coach (MCP import_activities) ------------------------------------------------------

/** One activity as the Strava connector reads it (Strava's own field names, distances in m, times in s). */
export interface StravaSummary {
  strava_id: string | number;
  sport_type: string;
  /** ISO 8601 start time with an offset or Z (Strava's start_date is UTC). */
  start_date: string;
  moving_time: number;
  elapsed_time?: number;
  distance?: number;
  total_elevation_gain?: number;
  average_heartrate?: number;
  max_heartrate?: number;
  name?: string;
}

export type SummaryResult =
  | { ok: true; stravaId: string; startedAt: number; name: string; activity: CardioActivity }
  | { ok: false; stravaId: string; reason: 'not_cardio' | 'invalid'; problem: string; sportType: string };

/** The earliest start an import accepts (Strava started in 2009). */
export const STRAVA_EPOCH = Date.UTC(2009, 0, 1);

/** A summary to our activity, with the same sport mapping as the export import (sportFromStrava). */
export function activityFromStravaSummary(s: StravaSummary, now = Date.now()): SummaryResult {
  const stravaId = String(s.strava_id ?? '').trim();
  const sportType = String(s.sport_type ?? '').slice(0, 60);
  const bad = (problem: string, reason: 'not_cardio' | 'invalid' = 'invalid'): SummaryResult => ({ ok: false, stravaId, reason, problem, sportType });
  if (!isStravaId(stravaId)) return bad('the Strava id must be the number from the activity link');
  const startedAt = /(z|[+-]\d\d:?\d\d)$/i.test(s.start_date ?? '') ? Date.parse(s.start_date) : NaN;
  if (!Number.isFinite(startedAt)) return bad('start_date must be an ISO time with Z or an offset, as Strava gives it');
  if (startedAt < STRAVA_EPOCH || startedAt > now + 60 * 60_000) return bad('the start time is before 2009 or in the future');
  const sport = sportFromStrava(sportType);
  if (!sport) return bad('not a sport this app tracks yet', 'not_cardio');
  const pos = (v: number | undefined) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : undefined);
  const moving = pos(s.moving_time) ?? pos(s.elapsed_time);
  if (!moving) return bad('no moving time');
  const elapsed = pos(s.elapsed_time);
  const dist = pos(s.distance);
  const gain = typeof s.total_elevation_gain === 'number' && Number.isFinite(s.total_elevation_gain) && s.total_elevation_gain >= 0 ? s.total_elevation_gain : undefined;
  const avgHr = pos(s.average_heartrate);
  const maxHr = pos(s.max_heartrate);
  const activity: CardioActivity = {
    v: 1, sport: sport.sport as CardioSport, variant: sport.variant, durationSec: Math.round(moving),
    ...(elapsed && elapsed >= moving ? { elapsedSec: Math.round(elapsed) } : {}),
    ...(dist ? { distanceM: Math.round(dist) } : {}),
    ...(gain !== undefined ? { elevationGainM: Math.round(gain) } : {}),
    ...(avgHr ? { avgHr: Math.round(avgHr) } : {}),
    ...(maxHr && (!avgHr || maxHr >= avgHr) ? { maxHr: Math.round(maxHr) } : {}),
    source: 'strava',
    stravaId,
  };
  const problem = activityProblem(activity);
  if (problem) return bad(problem);
  const name = (typeof s.name === 'string' ? s.name : '').replace(/\s+/g, ' ').trim().slice(0, 120);
  return { ok: true, stravaId, startedAt, name, activity };
}

/** An activity already in the history, as the duplicate checks need it. */
export interface KnownActivity {
  id: string;
  startedAt: number;
  activity?: Pick<CardioActivity, 'distanceM' | 'stravaId' | 'stravaUrl'>;
}

/**
 * Which known activity this one is: the same Strava id first, else the same start (2 minutes) and distance (3 %),
 * as in the file import. Null when it is new.
 */
export function findKnownActivity(known: KnownActivity[], me: { startedAt: number; activity: Pick<CardioActivity, 'distanceM' | 'stravaId'> }): { id: string; by: 'strava_id' | 'start_time' } | null {
  if (me.activity.stravaId) {
    const hit = known.find((k) => k.activity?.stravaId === me.activity.stravaId);
    if (hit) return { id: hit.id, by: 'strava_id' };
  }
  const mine = { startedAt: me.startedAt, ...(me.activity.distanceM !== undefined ? { distanceM: me.activity.distanceM } : {}) };
  const hit = known.find((k) => k.activity && (!k.activity.stravaId || !me.activity.stravaId) && sameActivity({ startedAt: k.startedAt, ...(k.activity.distanceM !== undefined ? { distanceM: k.activity.distanceM } : {}) }, mine));
  return hit ? { id: hit.id, by: 'start_time' } : null;
}
